import { LockOutlined } from '@ant-design/icons';
import { Tag } from 'antd';
import i18n from 'i18next';
import { flatten, groupBy, startCase } from 'lodash';
import { Fragment, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { None } from 'components/data/helpers';
import { type TDisplayColumnData } from 'components/data/types';
import { getFilterBy, getSortBy } from 'components/DataTable/paging';
import { type TQueryFilter } from 'components/DataTable/types';
import { resolveFilterBy } from 'components/DataTable/utils';
import { Spinner } from 'components/ui';
import { type AtlasGqlFilterBy, type AtlasGqlSortBy } from 'types/atlas-graphql';
import { uuidRegex as uuidRegexString } from 'utils/constants';

import { type TExtensionColumn, type TTableViewFilters } from '../types';
import { useNameMapping } from '../useNameMapping';
import {
  Description,
  Label,
  StyledFilterList,
  StyledList,
  StyledListContainer,
  StyledListItem,
  StyledOperator,
  StyledSortList,
} from '../Views.style';

const operatorCase = (value: string) => startCase(value.toLowerCase());

const BOOLEAN_LABELS: Record<string, string> = {
  true: i18n.t('common.true', 'True'),
  false: i18n.t('common.false', 'False'),
};

const formatFilters = (
  filters: AtlasGqlFilterBy[] | undefined,
  columnTitles: { [key: string]: React.ReactNode },
  mapping: { [key: string]: string },
  lockedKeys: string[]
) => {
  const groupedFilters = groupBy(filters ?? [], 'key');

  return Object.keys(groupedFilters).map((key) => {
    const filters = groupedFilters[key];

    const formattedValues: React.ReactNode[] = [];
    filters.forEach((filter, filter_idx) => {
      const { operator, values } = filter;

      // IS_NULL / IS_NOT_NULL carry no values; the operator alone describes the filter.
      if (!values.length) {
        formattedValues.push(
          <div key={`${key}_${filter_idx}`} style={{ display: 'flex' }}>
            <StyledOperator>{operatorCase(operator ?? '')}</StyledOperator>
          </div>
        );
        return;
      }

      // One row per filter, so a multi-value filter reads as one condition.
      formattedValues.push(
        <div
          key={`${key}_${filter_idx}`}
          style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', rowGap: 4 }}
        >
          <StyledOperator>{operatorCase(operator ?? '')}</StyledOperator>
          {values.map((val, val_idx) => (
            // The none option is sent as an empty-string value; show it as "none".
            <Tag key={val_idx}>{val ? (mapping[val] ?? BOOLEAN_LABELS[val] ?? val) : <None />}</Tag>
          ))}
        </div>
      );
    });

    const title = columnTitles[key] ?? startCase(key);
    return {
      label: lockedKeys.includes(key) ? (
        <>
          <LockOutlined /> {title}
        </>
      ) : (
        title
      ),
      value: formattedValues,
    };
  });
};

const formatSorts = (sorts: AtlasGqlSortBy[], columnTitles: { [key: string]: React.ReactNode }) => {
  return sorts.map(({ key, sort }) => {
    const formattedSort = sort === 'ASC' ? 'ASCENDING' : 'DESCENDING';
    return {
      label: columnTitles[key] ?? startCase(key),
      value: <Tag key={key}>{operatorCase(formattedSort)}</Tag>,
    };
  });
};

const formatColumns = (
  columns: TExtensionColumn[] | undefined,
  columnDefs: TDisplayColumnData[],
  columnTitles: { [key: string]: React.ReactNode }
) => {
  const hiddenColumns = (columns ?? []).filter(({ visible }) => visible === false);

  let columnsMoved = false;
  columns?.forEach((column, index) => {
    if (column.visible === true && column.key !== columnDefs?.[index]?.key) {
      columnsMoved = true;
    }
  });

  return [
    {
      label: i18n.t('table.columnsHidden', 'Columns Hidden'),
      value:
        hiddenColumns.length >= 1 ? (
          hiddenColumns
            .map(({ key }) => columnTitles[key] ?? startCase(key))
            .reduce((prev, curr) => [prev, ', ', curr])
        ) : (
          <None />
        ),
    },
    {
      label: i18n.t('table.columnsMoved', 'Columns Moved'),
      value: columnsMoved ? i18n.t('common.yes', 'Yes') : i18n.t('common.no', 'No'),
    },
  ];
};

const renderListItem = (item: { label: string; value: any }) => (
  <StyledListItem>
    <div>
      <Label>{item.label}:</Label>
      <Description>{item.value}</Description>
    </div>
  </StyledListItem>
);

type FilterConditionListProps = {
  header: React.ReactNode;
  filters: AtlasGqlFilterBy[];
  titles: { [key: string]: React.ReactNode };
  mapping?: { [key: string]: string };
  // Set by the page rather than the user, so their rows carry a lock
  lockedKeys?: string[];
};

export const FilterConditionList = ({
  header,
  filters,
  titles,
  mapping = {},
  lockedKeys = [],
}: FilterConditionListProps) => (
  <StyledFilterList
    size="small"
    header={header}
    dataSource={formatFilters(filters, titles, mapping, lockedKeys)}
    renderItem={renderListItem}
  />
);

type SortConditionListProps = {
  header: React.ReactNode;
  sorts: AtlasGqlSortBy[];
  titles: { [key: string]: React.ReactNode };
};

export const SortConditionList = ({ header, sorts, titles }: SortConditionListProps) => (
  <StyledSortList
    size="small"
    header={header}
    dataSource={formatSorts(sorts, titles)}
    renderItem={renderListItem}
  />
);

type SummaryProps = {
  tableView: TTableViewFilters;
  columnDefs: TDisplayColumnData[];
  lockedFilters?: TQueryFilter[];
};

export const Summary: React.FunctionComponent<SummaryProps> = ({
  tableView,
  columnDefs,
  lockedFilters = [],
}) => {
  const { t } = useTranslation();
  const { filters, sorts, multiSorts, columns } = useMemo(() => {
    return {
      filters: getFilterBy({
        filters: tableView.filters,
        columns: columnDefs,
        includeExcludedFromServerFilter: true,
      }) as AtlasGqlFilterBy[],
      sorts: getSortBy(tableView.sorts) as AtlasGqlSortBy[],
      multiSorts: (tableView.multiSorts ?? []) as AtlasGqlSortBy[],
      columns: tableView.columns ?? [],
    };
  }, [tableView, columnDefs]);

  const columnTitles = useMemo(
    () =>
      columnDefs.reduce((acc: { [key: string]: React.ReactNode }, col: TDisplayColumnData) => {
        const { key, title } = col;
        return { ...acc, [key]: <Fragment key={key}>{title}</Fragment> };
      }, {}),
    [columnDefs]
  );

  const uuidRegex = new RegExp(`^${uuidRegexString}$`, 'i');
  const filtersUuids = flatten([...lockedFilters, ...filters].map((f) => f.values)).filter(
    (f) => f && uuidRegex.test(f)
  );

  const { mapping, loading } = useNameMapping(filtersUuids as string[]);

  return loading ? (
    <Spinner />
  ) : (
    <StyledListContainer>
      {(lockedFilters.length > 0 || filters.length > 0) && (
        <FilterConditionList
          header={t('table.filters', 'Filters')}
          // A column filter matching a locked one shows once, as locked. TQueryFilter types the
          // operator as a plain string.
          filters={resolveFilterBy(lockedFilters, filters as TQueryFilter[]) as AtlasGqlFilterBy[]}
          titles={columnTitles}
          mapping={mapping}
          lockedKeys={lockedFilters.map(({ key }) => key)}
        />
      )}
      {(sorts.length > 0 || multiSorts.length > 0) && (
        <SortConditionList
          header={t('table.sorts', 'Sorts')}
          sorts={[...sorts, ...multiSorts]}
          titles={columnTitles}
        />
      )}
      {columns.length > 0 && (
        <StyledList
          size="small"
          header={t('table.customizedColumns', 'Customized Columns')}
          dataSource={formatColumns(columns, columnDefs, columnTitles)}
          renderItem={renderListItem}
        />
      )}
    </StyledListContainer>
  );
};
